import { useSearchParams } from "#routers/hooks";
import {
  CreateAssetDropdownMenu,
  FlowsTablePanel,
  RecordsListTablePanel,
} from "#src/batteries-included-components";
import { ApplyAutomationProgressBanner } from "#src/batteries-included-components/Banners/ApplyAutomationProgressBanner";
import { ImportDataActionStatusWithQueue } from "#src/batteries-included-components/Banners/ImportDataActionStatus";
import { TemplatedConfigurationRunStatusWithQueue } from "#src/batteries-included-components/Banners/TemplatedConfigurationRunStatus";
import { getImportDataActionQueueKey } from "#src/batteries-included-components/Buttons/ImportDataAction/ImportDataAction.helpers";
import { FlowsViewFilterArea } from "#src/batteries-included-components/FilterAreas/AssetsFilterAreas";
import {
  FlowRecordsTableFilterArea,
  FlowRecordsViewFilterArea,
} from "#src/batteries-included-components/FilterAreas/RecordsFilterAreas";
import { useIsFeatureAvailable } from "#src/contexts/AuthenticatedContext.helpers";
import useLocalization from "#src/hooks/useLocalization";
import { useStorageKey } from "#src/hooks/useStorageKey";
import { useBreadcrumbsFromRoute } from "#utils/route";
import {
  generateDocumentTitle,
  Page,
  Tab,
} from "@validereinc/common-components";
import { AssetType, ResourceDefinitions, Resources } from "@validereinc/domain";
import React from "react";
import { Titled } from "react-titled";
import { FlowListRoute } from "./";

const DEFAULT_TAB_KEY = "list";

export const FlowsListPage = () => {
  const [isDataIngestionEnabled] = useIsFeatureAvailable({
    featureFlagQuery: "core:data_pipeline",
  });
  const { localize, isLoading } = useLocalization();
  const [searchParams, setSearchParams] = useSearchParams();
  const [calcsAndRecordsSharedViewConfigStorageKey] = useStorageKey(
    ["view-configuration"],
    "calculations-records-shared"
  );
  const [assetsSharedViewConfigStorageKey] = useStorageKey(
    ["view-configuration"],
    "assets-shared"
  );
  const [
    filterConfigStorageKey,
    tableConfigStorageKey,
    tableStateStorageKey,
    groupByTablesStateStorageKey,
  ] = useStorageKey(
    [
      "filter-configuration",
      "table-configuration",
      "table-state",
      "group-by-tables-state",
    ],
    ["flows", "list"]
  );
  const [
    recordsFilterConfigStorageKey,
    recordsTableConfigStorageKey,
    recordsTableStateStorageKey,
  ] = useStorageKey(
    ["filter-configuration", "table-configuration", "table-state"],
    ["records", "list", "flows"]
  );
  const [recordsApplyAutomationJobStorageKey] = useStorageKey(
    ["async-job"],
    ["records", "list", "flows", "apply-automation"]
  );
  const [configsApplyAutomationJobStorageKey] = useStorageKey(
    ["async-job"],
    "flows-configs-apply-automation"
  );

  const actionRow = <CreateAssetDropdownMenu assetType={Resources.FLOW} />;

  const [breadcrumbs] = useBreadcrumbsFromRoute(FlowListRoute);
  const flowsImportDataActionQueueKey = getImportDataActionQueueKey({
    resource: {
      ...ResourceDefinitions.flow,
      label: {
        singular: localize(ResourceDefinitions.flow.label.singular),
        plural: localize("flow_plural"),
      },
    },
  });
  const recordsImportDataActionQueueKey = getImportDataActionQueueKey({
    resource: {
      ...ResourceDefinitions.record,
      label: {
        singular: `${localize(ResourceDefinitions.flow.label.singular)} ${ResourceDefinitions.record.label.singular}`,
        plural: `${localize(ResourceDefinitions.flow.label.singular)} ${ResourceDefinitions.record.label.plural}`,
      },
    },
  });

  return (
    <Page
      breadcrumbs={breadcrumbs}
      category={FlowListRoute.subtitle}
      title={`${localize(FlowListRoute.title)}`}
      actionRow={actionRow}
      activeTabKey={searchParams?.tab ?? DEFAULT_TAB_KEY}
      onActiveTabKeyChange={(tabKey: string) =>
        setSearchParams({ tab: tabKey })
      }
      isLoading={isLoading}
    >
      <Tab
        title="List"
        tabKey="list"
      >
        <TemplatedConfigurationRunStatusWithQueue
          primaryResourceType={Resources.FLOW}
        />
        {isDataIngestionEnabled ? (
          <ImportDataActionStatusWithQueue
            queueKey={flowsImportDataActionQueueKey}
          />
        ) : null}
        <FlowsViewFilterArea
          viewConfigStorageKey={assetsSharedViewConfigStorageKey}
        />
        <FlowsTablePanel
          filterConfigStorageKey={filterConfigStorageKey}
          tableConfigStorageKey={tableConfigStorageKey}
          tableStateStorageKey={tableStateStorageKey}
          groupByTablesStateStorageKey={groupByTablesStateStorageKey}
          viewConfigStorageKey={assetsSharedViewConfigStorageKey}
          isWithCustomAttributes
          isWithTableActions
        />
      </Tab>
      <Tab
        title="Records"
        tabKey="records"
      >
        <Titled
          title={generateDocumentTitle(
            `${breadcrumbs.at(-1)?.title ?? localize(Resources.FLOW)} ${ResourceDefinitions.record.label.plural}`
          )}
        >
          {isDataIngestionEnabled ? (
            <ImportDataActionStatusWithQueue
              queueKey={recordsImportDataActionQueueKey}
            />
          ) : null}
          <ApplyAutomationProgressBanner
            jobStorageKey={recordsApplyAutomationJobStorageKey}
            showCounts={true}
          />
          <ApplyAutomationProgressBanner
            jobStorageKey={configsApplyAutomationJobStorageKey}
            showCounts={false}
          />
          <FlowRecordsViewFilterArea
            viewConfigStorageKey={calcsAndRecordsSharedViewConfigStorageKey}
          />
          <RecordsListTablePanel
            type={AssetType.FLOW}
            filterConfigStorageKey={recordsFilterConfigStorageKey}
            tableConfigStorageKey={recordsTableConfigStorageKey}
            tableStateStorageKey={recordsTableStateStorageKey}
            viewConfigStorageKey={calcsAndRecordsSharedViewConfigStorageKey}
            applyAutomationJobStorageKey={recordsApplyAutomationJobStorageKey}
            applySeededAutomationJobStorageKey={
              configsApplyAutomationJobStorageKey
            }
            filterComponentSlot={({ recordValueTypes }) => (
              <FlowRecordsTableFilterArea
                filterConfigStorageKey={recordsFilterConfigStorageKey}
                selectableProperties={recordValueTypes}
              />
            )}
          />
        </Titled>
      </Tab>
    </Page>
  );
};
